import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../config/api';

const ADMIN_DOMAIN = '@typco.com';

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [countyId, setCountyId] = useState('');
  const [counties, setCounties] = useState([]);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');
  const [domainError, setDomainError] = useState(false);

  const normalisedEmail = email.trim().toLowerCase();
  const isAdminEmail = useMemo(() => normalisedEmail.endsWith(ADMIN_DOMAIN), [normalisedEmail]);

  useEffect(() => {
    apiClient
      .get('/lookup/counties')
      .then(res => setCounties(res.data ?? []))
      .catch(err => console.error('Failed to fetch counties:', err));
  }, []);

  useEffect(() => {
    if (isAdminEmail && countyId) {
      setCountyId('');
    }
  }, [isAdminEmail, countyId]);

  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;

    setMessage('');
    setSuccess(false);
    setDomainError(false);

    if (!normalisedEmail) {
      setMessage('Email is required.');
      return;
    }

    if (!password.trim()) {
      setMessage('Password is required.');
      return;
    }

    if (!isAdminEmail && !countyId) {
      setMessage('Please select your county.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        email: normalisedEmail,
        password,
      };

      if (!isAdminEmail) {
        payload.countyId = parseInt(countyId, 10);
      }

      await apiClient.post('/auth/register', payload);

      setSentToEmail(normalisedEmail);
      setSuccess(true);
      setPassword('');
      setEmail('');
      setCountyId('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Registration failed. Please try again.';
      const isDomainUnauthorized =
        err.response?.status === 403 &&
        typeof errorMsg === 'string' &&
        errorMsg.toLowerCase().includes('domain');

      if (isDomainUnauthorized) {
        setDomainError(true);
        setMessage('');
      } else {
        setMessage(errorMsg);
        setDomainError(false);
      }

      setSuccess(false);
      setSentToEmail('');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.successStandalone}>
          <h3 style={styles.successHeading}>Registration Complete</h3>
          <p style={styles.successBody}>
            User registered successfully. A verification email has been sent to
            <span style={styles.successEmail}> {sentToEmail || 'your email address'}</span>.
            Please click the link in that message to verify your account.
          </p>
          <p style={styles.successSupport}>
            Didn't receive the mail? Contact{' '}
            <a href="mailto:helpdesk@typco.com" style={styles.supportLink}>helpdesk@typco.com</a> for assistance.
          </p>
          <p style={styles.successSupport}>
            Ready to continue? <a href="/login" style={styles.supportLink}>Return to login</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {domainError && (
          <div style={styles.domainErrorContainer}>
            <p style={styles.domainErrorText}>
              Email is not authorized.<br />
              Please contact helpdesk@typco.com for support.
            </p>
          </div>
        )}

        {message && (
          <div style={{ ...styles.alert, ...styles.alertError }}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label} htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            autoComplete="email"
            required
          />

          <label style={styles.label} htmlFor="password">Password</label>
          <div style={styles.inputGroup}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter a strong password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={styles.passwordToggle}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={styles.passwordIcon}
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.05-2.99 3.14-5.41 5.76-6.84" />
                  <path d="M1 1l22 22" />
                  <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={styles.passwordIcon}
                >
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          {!isAdminEmail && (
            <>
              <label style={styles.label} htmlFor="county">County</label>
              <select
                id="county"
                value={countyId}
                onChange={(e) => setCountyId(e.target.value)}
                style={styles.select}
                required
              >
                <option value="" disabled>Select your county</option>
                {counties.map(county => (
                  <option key={county.CountyID} value={county.CountyID}>
                    {county.Name}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div style={styles.loginLinkWrapper}>
          <span style={styles.loginPrompt}>Already have an account?</span>{' '}
          <a href="/login" style={styles.loginLink}>Go to Login</a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f162b, #1f3c88)',
    fontFamily: 'Segoe UI, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    backgroundColor: '#ffffff',
    borderRadius: '18px',
    boxShadow: '0 24px 48px rgba(15, 22, 43, 0.25)',
    padding: '36px',
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#475467',
    display: 'block',
    paddingTop: '8px',
  },
  inputGroup: {
    position: 'relative',
  },
  input: {
    width: '92%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  },
  select: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '15px',
    backgroundColor: '#fff',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: "linear-gradient(45deg, transparent 50%, #1f3c88 50%), linear-gradient(135deg, #1f3c88 50%, transparent 50%)",
    backgroundPosition: 'calc(100% - 15px) calc(50% - 4px), calc(100% - 10px) calc(50% - 4px)',
    backgroundSize: '5px 5px, 5px 5px',
    backgroundRepeat: 'no-repeat',
  },
  adminHint: {
    fontSize: '14px',
    color: '#1f3c88',
    marginTop: '-8px',
  },
  button: {
    marginTop: '8px',
    padding: '14px',
    borderRadius: '999px',
    border: 'none',
    background: 'linear-gradient(120deg, #1f3c88, #39a0ed)',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  passwordToggle: {
    position: 'absolute',
    top: '50%',
    right: '12px',
    transform: 'translateY(-50%)',
    appearance: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    color: '#1f3c88',
  },
  passwordIcon: {
    width: '20px',
    height: '20px',
  },
  alert: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginBottom: '24px',
    fontSize: '15px',
    fontWeight: 600,
  },
  alertError: {
    backgroundColor: '#fdecea',
    color: '#b02a37',
  },
  domainErrorContainer: {
    backgroundColor: '#fdecec',
    border: '1px solid #f8b4b4',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  domainErrorText: {
    margin: 0,
    color: '#b42318',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  successStandalone: {
    width: '100%',
    maxWidth: '520px',
    background: 'linear-gradient(135deg, #e8f4ff, #f5fbff)',
    border: '1px solid #c7e0ff',
    borderRadius: '18px',
    boxShadow: '0 24px 48px rgba(15, 22, 43, 0.25)',
    padding: '36px',
    boxSizing: 'border-box',
    textAlign: 'left',
  },
  successHeading: {
    margin: '0 0 12px',
    fontSize: '24px',
    fontWeight: 700,
    color: '#123872',
  },
  successBody: {
    margin: '0 0 16px',
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#2d3a4b',
  },
  successEmail: {
    fontWeight: 700,
    color: '#1f3c88',
  },
  successSupport: {
    margin: '0 0 12px',
    fontSize: '14px',
    color: '#344054',
  },
  supportLink: {
    color: '#1f3c88',
    fontWeight: 600,
  },
  loginLinkWrapper: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#475467',
  },
  loginPrompt: {
    fontWeight: 500,
  },
  loginLink: {
    color: '#1f3c88',
    fontWeight: 600,
    textDecoration: 'underline',
  },
};

export default RegisterForm;
